import { useMemo, useState } from 'react';
import type { MouseEventHandler, ReactNode } from 'react';

import {
  RESET_PASSWORD_BUTTON_TEST_ID,
  RESET_PASSWORD_MODAL_TEST_ID
} from '@pages/UserManagement/constants';

import { useForgotPasswordMutation } from 'reduxLocal/api/authenticationApi';

import { TertiaryButton } from '@atoms/buttons/TertiaryButton';

import { ContactSupportCTA } from './ContactSupportCTA';
import { FeedbackModal } from './FeedbackModal';
import { userManagementSharedT as translations } from './translations';

type Props = {
  email: string;
};

type ResultState =
  | { open: false }
  | { open: true; variant: 'success' | 'error'; message: ReactNode };

export const ResetPasswordButton = ({ email }: Props) => {
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const [result, setResult] = useState<ResultState>({ open: false });

  const handleResetPassword: MouseEventHandler<HTMLButtonElement> = async (
    event
  ) => {
    event.preventDefault();

    try {
      await forgotPassword({ email }).unwrap();

      setResult({
        open: true,
        variant: 'success',
        message: (
          <div className="space-y-3">
            <p>{translations.resetPassword.success.body1}</p>
            <p>{translations.resetPassword.success.body2}</p>
            <ContactSupportCTA
              reachOutPrefix={translations.resetPassword.success.reachOut}
            />
          </div>
        )
      });
    } catch (_) {
      setResult({
        open: true,
        variant: 'error',
        message: (
          <div className="space-y-3">
            <p>{translations.resetPassword.error.body1}</p>
            <ContactSupportCTA
              reachOutPrefix={translations.resetPassword.error.reachOut}
            />
          </div>
        )
      });
    }
  };

  const modalTitle = useMemo(() => {
    if (!result.open) return '';
    return result.variant === 'success'
      ? translations.resetPassword.success.title
      : translations.resetPassword.error.title;
  }, [result]);

  const handleCloseModal = () => setResult({ open: false });

  return (
    <>
      <TertiaryButton
        size="small"
        className="w-full"
        onClick={handleResetPassword}
        disabled={isLoading}
        data-testid={RESET_PASSWORD_BUTTON_TEST_ID}
      >
        {translations.resetPassword.actions.buttonLabel}
      </TertiaryButton>

      {result.open && (
        <FeedbackModal
          testId={RESET_PASSWORD_MODAL_TEST_ID}
          open={result.open}
          title={modalTitle}
          message={result.message}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};
